import { getCoreUtils } from "./kCoreErrorRegistry";   // not kCoreHandler: that file's big tables are 47 KB a small (loader) bundle must not pay

/**
 * kBrowserSettingsCore - a person's small remembered settings in `localStorage` (or any storage with the same three calls), with PLAIN RESULTS.
 *
 * WHERE IT RUNS. The storage is INJECTED (a page passes `window.localStorage`, usually through `kBrowserStorage.LocalStorageOrNull()` in ReUsableTS, a
 * test passes `{ getItem, setItem, removeItem }` over a plain object, a page that blocks storage passes null). Nothing here touches `window`, so it is
 * safe in Node and in a worker.
 *
 * WHY IT EXISTS (SharePoint reuse programme 2026-10-05). DeckBuilder had eight places that did the same dance by hand: try `localStorage` (which THROWS in
 * a blocked or sandboxed page), tell "missing" from "empty", parse JSON that a person or an old build may have damaged, fall back to the defaults, keep the
 * choice in memory for the life of the page when the browser refuses the write, never turn a refusal into a defect report. `ks.LocalStorageStore` /
 * `LocaleStorageLoad` (ReUsableTS) chunk big strings and give none of that.
 *
 * WHAT YOU GET
 *   - a KEY PREFIX (`Prefix`), so one product's keys are recognisable and clearable
 *   - `ReadText` / `WriteText`: missing (null, outcome "missing") is not empty ("" stays ""); a refused write says why and is KEPT IN MEMORY for this page
 *     (`MemoryFallback`, default true), so the next read in the same page still sees the choice; the person is never told a preference "failed"
 *   - a SIZE GUARD (`MaxChars`, 0 = none): a value over it is refused up front (outcome "tooBig"), nothing is written
 *   - `ReadJson` / `WriteJson` with an optional validator (`Check`: whatever it does not accept is "damaged" and the fallback is returned) and an
 *     optional SCHEMA VERSION: with `SchemaVersion` > 0 the value is stored as `{ "v": N, "d": value }`; a stored value of another version goes to
 *     `Migrate(oldVersion, data)` (return the new data, or null to drop it) before the fallback is used. With 0 (default) the JSON is stored as it is,
 *     so existing keys written by older builds keep reading.
 *   - nothing here is reported centrally for a storage refusal: that is an expected, defined outcome. Only a PROGRAMMING error (a value that cannot be
 *     serialised) is reported, then answered as outcome "failed".
 *
 * What a second consumer would change: the prefix, the schema version, the size guard, the validator (all arguments / properties).
 */
export interface kKeyValueStorageLike {
    getItem: (Key: string) => string | null;
    setItem: (Key: string, Value: string) => void;
    removeItem: (Key: string) => void;
}

export type kSettingOutcome = "ok" | "missing" | "damaged" | "oldVersion" | "noStorage" | "tooBig" | "refused" | "failed" | "stopped";

/** The answer of a read. `Value` is always usable (the fallback when the outcome is not "ok"). */
export class kSettingRead<T> {
    public Outcome: kSettingOutcome;
    public Value: T;
    public Message: string;

    public constructor(Outcome: kSettingOutcome, Value: T, Message: string) {
        this.Outcome = Outcome;
        this.Value = Value;
        this.Message = Message;
    }
}

/** The answer of a write: `Saved` is true only when the storage took it. `KeptInMemory` is true when the page keeps the value anyway. */
export class kSettingWrite {
    public Saved: boolean;
    public Outcome: kSettingOutcome;
    public KeptInMemory: boolean;
    public Message: string;

    public constructor(Saved: boolean, Outcome: kSettingOutcome, KeptInMemory: boolean, Message: string) {
        this.Saved = Saved;
        this.Outcome = Outcome;
        this.KeptInMemory = KeptInMemory;
        this.Message = Message;
    }
}

export class kBrowserSettingsCore {
    /** A write the storage refuses (or a page without storage) keeps the value for the life of the page. */
    public MemoryFallback: boolean = true;
    /** Largest value in characters; 0 = no limit. */
    public MaxChars: number = 0;
    /** `{ v: N, d: value }` envelope for JSON when > 0; 0 = the JSON is stored as it is. */
    public SchemaVersion: number = 0;
    /** Turns data of an older schema into the current one (null drops it). Only consulted when `SchemaVersion` > 0. */
    public Migrate: ((OldVersion: number, Data: unknown) => unknown) | null = null;
    private _Storage: kKeyValueStorageLike | null;
    private _Prefix: string;
    private _Memory: { [Key: string]: string } = {};

    public constructor(Storage: kKeyValueStorageLike | null, Prefix: string = "") {
        this._Storage = Storage;
        this._Prefix = Prefix;
    }

    /**
     * Settings over a storage that is FETCHED BY A GETTER, for a page whose code must not import the browser-only `kBrowserStorage` (ReUsableTS) and for
     * storages other than localStorage (`() => window.sessionStorage`, `() => globalThis.localStorage`). The getter is called ONCE, here, and the access
     * itself may throw (blocked site data): a throwing getter, or one that answers undefined or null, gives page-memory settings with no storage.
     * Nothing here names `window`, so it stays safe in Node.
     */
    public static FromAccessor = (Get: () => kKeyValueStorageLike | null | undefined, Prefix: string = ""): kBrowserSettingsCore => {
        if (getCoreUtils().KErrorMode === true) { return new kBrowserSettingsCore(null, Prefix); }
        try {
            const Found: kKeyValueStorageLike | null | undefined = Get();
            if (Found === undefined || Found === null) { return new kBrowserSettingsCore(null, Prefix); }
            return new kBrowserSettingsCore(Found, Prefix);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: merely reading a blocked storage throws in some browsers; "no storage" is the defined state (settings then last for this visit or fall back to their defaults).
            return new kBrowserSettingsCore(null, Prefix);
        }
    };

    public KeyOf = (Name: string): string => {
        // DOCUMENTED EXCEPTION: a concatenation. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this._Prefix + Name;
    };

    /** True when there is a storage to ask (it may still refuse). */
    public HasStorage = (): boolean => {
        // DOCUMENTED EXCEPTION: a field test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this._Storage !== null;
    };

    // ---- text ---------------------------------------------------------------------------------------------------------------------

    /** The stored text. `Value` is null when nothing is stored (outcome "missing"), which is NOT the same as "". */
    public ReadText = (Name: string): kSettingRead<string | null> => {
        if (getCoreUtils().KErrorMode === true) { return new kSettingRead<string | null>("stopped", null, "stopped"); }
        try {
            const Key: string = this.KeyOf(Name);
            if (Object.prototype.hasOwnProperty.call(this._Memory, Key) === true) { return new kSettingRead<string | null>("ok", this._Memory[Key], "kept in memory"); }
            if (this._Storage === null) { return new kSettingRead<string | null>("noStorage", null, "this page has no storage"); }
            const Text: string | null = this.Fetch(this._Storage, Key);
            if (Text === null) { return new kSettingRead<string | null>("missing", null, "nothing is stored"); }
            return new kSettingRead<string | null>("ok", Text, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.ReadText");
            return new kSettingRead<string | null>("failed", null, "the setting could not be read");
        }
    };

    /** DOCUMENTED EXCEPTION: delegates to the safe storage read; blocked reads mean absent. */
    private Fetch = (Storage: kKeyValueStorageLike, Key: string): string | null => {
        return kBrowserSettingsCore.SafeGet(Storage, Key);
    };

    /** DOCUMENTED EXCEPTION: a blocked storage read is handled as absent; callers own error gates. */
    public static SafeGet = (Storage: kKeyValueStorageLike, Key: string): string | null => {
        try {
            return Storage.getItem(Key);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: reading a blocked storage throws in some browsers; "nothing stored" is the correct, defined answer (the caller uses its default).
            return null;
        }
    };

    public WriteText = (Name: string, Text: string): kSettingWrite => {
        if (getCoreUtils().KErrorMode === true) { return new kSettingWrite(false, "stopped", false, "stopped"); }
        try {
            const Key: string = this.KeyOf(Name);
            if (this.MaxChars > 0 && Text.length > this.MaxChars) {
                return new kSettingWrite(false, "tooBig", false, "the value is " + String(Text.length) + " characters, over the limit of " + String(this.MaxChars));
            }
            if (this._Storage === null) { return this.Keep(Key, Text, "noStorage", "this page has no storage"); }
            const Refusal: string = this.Put(this._Storage, Key, Text);
            if (Refusal !== "") { return this.Keep(Key, Text, "refused", Refusal); }
            delete this._Memory[Key];
            return new kSettingWrite(true, "ok", false, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.WriteText");
            return new kSettingWrite(false, "failed", false, "the setting could not be written");
        }
    };

    /** The storage's own refusal (quota, disabled) as a sentence, or "" when it took the value. */
    private Put = (Storage: kKeyValueStorageLike, Key: string, Text: string): string => {
        try {
            Storage.setItem(Key, Text);
            return "";
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a refused write (quota, storage disabled) is an EXPECTED answer: it is returned to the caller as a refusal sentence, never lost.
            return "the browser storage refused the write (quota or disabled): " + this.ReasonOf(e);
        }
    };

    private ReasonOf = (Failure: unknown): string => {
        // DOCUMENTED EXCEPTION: private pure helper; every caller (ReadJson / WriteText) is guarded and wrapped. ERROR-SUPPRESSED-JUSTIFIED: deterministic, callers carry the full pattern.
        if (Failure !== null && typeof Failure === "object" && typeof (Failure as { message?: unknown }).message === "string") { return (Failure as { message: string }).message; }
        return String(Failure);
    };

    private Keep = (Key: string, Text: string, Outcome: kSettingOutcome, Message: string): kSettingWrite => {
        // DOCUMENTED EXCEPTION: private pure helper; every caller (ReadJson / WriteText) is guarded and wrapped. ERROR-SUPPRESSED-JUSTIFIED: deterministic, callers carry the full pattern.
        if (this.MemoryFallback === false) { return new kSettingWrite(false, Outcome, false, Message); }
        this._Memory[Key] = Text;
        return new kSettingWrite(false, Outcome, true, Message);
    };

    /** Forgets the stored value and the in-memory copy. Returns false when the storage refused the removal (the memory copy is gone anyway). */
    public Remove = (Name: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Key: string = this.KeyOf(Name);
            delete this._Memory[Key];
            if (this._Storage === null) { return false; }
            return this.Drop(this._Storage, Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.Remove");
            return false;
        }
    };

    private Drop = (Storage: kKeyValueStorageLike, Key: string): boolean => {
        try {
            Storage.removeItem(Key);
            return true;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a storage that refuses a removal is a defined state, answered as false; the in-memory copy is already gone.
            return false;
        }
    };

    // ---- JSON ---------------------------------------------------------------------------------------------------------------------

    /**
     * The stored JSON. `Check` (optional) accepts or cleans the parsed value (return null to reject it: outcome "damaged"). Anything not usable returns the
     * `Fallback` with the outcome saying why ("missing", "damaged", "oldVersion", "noStorage").
     */
    public ReadJson = <T>(Name: string, Fallback: T, Check: ((Raw: unknown) => T | null) | null = null): kSettingRead<T> => {
        if (getCoreUtils().KErrorMode === true) { return new kSettingRead<T>("stopped", Fallback, "stopped"); }
        try {
            const Text: kSettingRead<string | null> = this.ReadText(Name);
            if (Text.Value === null) { return new kSettingRead<T>(Text.Outcome, Fallback, Text.Message); }
            const Parsed: { Ok: boolean; Data: unknown } = this.Parse(Text.Value);
            if (Parsed.Ok === false) { return new kSettingRead<T>("damaged", Fallback, "the stored value is not valid JSON"); }
            return this.Unwrap(Parsed.Data, Fallback, Check);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.ReadJson");
            return new kSettingRead<T>("failed", Fallback, "the setting could not be read");
        }
    };

    private Parse = (Text: string): { Ok: boolean; Data: unknown } => {
        try {
            return { Ok: true, Data: JSON.parse(Text) };
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a damaged or hand-edited value is a defined outcome ("damaged", the fallback is used), not a defect of this code.
            return { Ok: false, Data: null };
        }
    };

    /** The schema envelope (when versioned), the migration of an older one, then the validator. */
    private Unwrap = <T>(Parsed: unknown, Fallback: T, Check: ((Raw: unknown) => T | null) | null): kSettingRead<T> => {
        // DOCUMENTED EXCEPTION: private pure helper; every caller (ReadJson / WriteText) is guarded and wrapped. ERROR-SUPPRESSED-JUSTIFIED: deterministic, callers carry the full pattern.
        let Data: unknown = Parsed;
        if (this.SchemaVersion > 0) {
            const Envelope: { v?: unknown; d?: unknown } | null = this.EnvelopeOf(Parsed);
            if (Envelope === null) { return new kSettingRead<T>("damaged", Fallback, "the stored value has no schema version"); }
            Data = Envelope.d;
            if (Envelope.v !== this.SchemaVersion) {
                Data = this.Migrated(Envelope.v, Envelope.d);
                if (Data === null) { return new kSettingRead<T>("oldVersion", Fallback, "the stored value is of another version"); }
            }
        }
        if (Check === null) { return new kSettingRead<T>("ok", Data as T, ""); }
        const Clean: T | null = Check(Data);
        if (Clean === null) { return new kSettingRead<T>("damaged", Fallback, "the stored value is not acceptable"); }
        return new kSettingRead<T>("ok", Clean, "");
    };

    private EnvelopeOf = (Parsed: unknown): { v?: unknown; d?: unknown } | null => {
        // DOCUMENTED EXCEPTION: private pure helper; every caller (ReadJson / WriteText) is guarded and wrapped. ERROR-SUPPRESSED-JUSTIFIED: deterministic, callers carry the full pattern.
        if (Parsed === null || typeof Parsed !== "object" || Array.isArray(Parsed) === true) { return null; }
        const Bag: { v?: unknown; d?: unknown } = Parsed as { v?: unknown; d?: unknown };
        if (typeof Bag.v !== "number") { return null; }
        return Bag;
    };

    private Migrated = (OldVersion: unknown, Data: unknown): unknown => {
        // DOCUMENTED EXCEPTION: private pure helper; every caller (ReadJson / WriteText) is guarded and wrapped. ERROR-SUPPRESSED-JUSTIFIED: deterministic, callers carry the full pattern.
        if (this.Migrate === null || typeof OldVersion !== "number") { return null; }
        return this.Migrate(OldVersion, Data);
    };

    public WriteJson = (Name: string, Value: unknown): kSettingWrite => {
        if (getCoreUtils().KErrorMode === true) { return new kSettingWrite(false, "stopped", false, "stopped"); }
        try {
            let Payload: unknown = Value;
            if (this.SchemaVersion > 0) { Payload = { v: this.SchemaVersion, d: Value }; }
            return this.WriteText(Name, JSON.stringify(Payload));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.WriteJson");
            return new kSettingWrite(false, "failed", false, "the value could not be turned into JSON");
        }
    };

    /** Forgets only the in-memory copies (a test, a "reset to defaults" that wants the storage read again). */
    public ForgetMemory = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this._Memory = {};
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserSettingsCore.ForgetMemory");
        }
    };
}
